import { useState } from 'react';
import { OpponentFileScreen } from './OpponentFileScreen';
import { useOpponents } from './useOpponents';

/**
 * B spec §2.4 — **the Opponents tab** (build unit B-6, scenario 1's browse half): the team
 * cards from `home.loadHome`, each opening that opponent's file.
 *
 * The file is view state inside the tab (list ↔ file with a `from` pointer, §2.2's navigation
 * model), not a shell overlay: it is a reading surface the tab bar should stay under — setup
 * and the match record hide the bar for their own reasons (keyboard, takeover), neither of
 * which applies here. Opening a match FROM the file goes up through {@link OpponentsScreenProps.onOpenRecord}
 * to the shell, which owns what a match record is (B-18's seam).
 *
 * Creating an opponent is deliberately absent: teams are created where they mean something —
 * match setup's "pick or create" (B-7, `preMatch.createTeam`). This tab reads.
 */

export interface OpponentsScreenProps {
  readonly onOpenRecord: (matchId: string) => void;
}

type View = { readonly kind: 'list' } | { readonly kind: 'file'; readonly teamId: string };

export function OpponentsScreen({ onOpenRecord }: OpponentsScreenProps) {
  const [view, setView] = useState<View>({ kind: 'list' });

  if (view.kind === 'file')
    return (
      <OpponentFileScreen
        teamId={view.teamId}
        onBack={() => {
          setView({ kind: 'list' });
        }}
        onOpenRecord={onOpenRecord}
      />
    );

  return <OpponentsList onOpen={(teamId) => { setView({ kind: 'file', teamId }); }} />;
}

function OpponentsList({ onOpen }: { readonly onOpen: (teamId: string) => void }) {
  const { state } = useOpponents();

  if (state.phase === 'loading') return <p className="homeb-empty">Reading your opponents…</p>;
  if (state.phase === 'failed')
    return (
      <p className="homeb-empty" role="alert">
        Your opponents could not be read. The log has the detail.
      </p>
    );

  return (
    <div
      className="opponents"
      data-testid="opponents"
      data-read={String(state.reads)}
      data-teams={String(state.teams.length)}
    >
      <h1 className="opponents__title">Opponents</h1>

      {state.teams.length === 0 ? (
        <p className="homeb-empty" data-testid="opponents-empty">
          No opponents yet. A team joins this list when you set up a match against them.
        </p>
      ) : (
        <ul className="opponents-list" data-testid="opponents-list">
          {state.teams.map((team) => (
            <li key={team.teamId}>
              <button
                type="button"
                className="opponents-card"
                data-testid={`opponents-team-${team.teamId}`}
                onClick={() => {
                  onOpen(team.teamId);
                }}
              >
                <span className="opponents-card__name">{team.name}</span>
                {(team.club !== null || team.ageGroup !== null) && (
                  <span className="opponents-card__sub">
                    {[team.club, team.ageGroup].filter(Boolean).join(' · ')}
                  </span>
                )}
                <span className="opponents-card__meta">
                  {team.matchCount === 0
                    ? 'No matches yet'
                    : `${String(team.matchCount)} match${team.matchCount === 1 ? '' : 'es'}`}
                  {team.lastPlayed !== null && ` · last ${team.lastPlayed}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
